import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LineChart, Line, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import 'animate.css';
import './DashboardAdmin.css';

// =========================================================================
// 🌟 CONFIGURACIÓN DE URL BASE - CAMBIA ESTO POR TU ENLACE REAL DE RENDER 🌟
// =========================================================================
const BACKEND_URL = 'https://fronted-bambo-s-pj4s.vercel.app'.includes('vercel') 
  ? 'https://backend-bambos.onrender.com' // 👈 PON AQUÍ TU URL DE RENDER (Ejemplo: https://bambo-backend.onrender.com)
  : 'http://127.0.0.1:8000';

// ===================================================
// --- SUB-COMPONENTE: GENERACIÓN DE REPORTES CON VISTA PREVIA (IA + COOLDOWN) ---
// ===================================================
const SeccionReportesIA = () => {
  const [preguntaIA, setPreguntaIA] = useState('');
  const [cargando, setCargando] = useState(false);
  const [cargandoExcel, setCargandoExcel] = useState(false);
  const [datosReporte, setDatosReporte] = useState(null); 

  // --- Estados para el Cooldown (Temporizador de Espera) ---
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);

  // Efecto para manejar la cuenta regresiva del reloj paso a paso
  useEffect(() => {
    if (cooldown > 0) {
      timerRef.current = setTimeout(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    } else {
      clearTimeout(timerRef.current);
    }
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  // 1. Pedirle a la IA que procese la instrucción y devuelva la estructura de la tabla
  const consultarAsistenteIA = async (e) => {
    e.preventDefault();
    if (!preguntaIA.trim()) return alert("Por favor ingresa una instrucción para el asistente.");
    if (cooldown > 0) return; // Protección extra en caso de saltarse el disabled

    setCargando(true);
    setDatosReporte(null); // Limpiamos la vista previa anterior

    try {
      const response = await fetch(`${BACKEND_URL}/api/reportes/ia-vista-previa/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: preguntaIA })
      });

      if (response.ok) {
        const data = await response.json();
        setDatosReporte(data);
      } else if (response.status === 429) {
        alert("⚠️ Cuota de peticiones agotada temporalmente. Espera a que termine el temporizador para reintentar.");
      } else {
        alert("❌ El asistente no pudo procesar la consulta. Intenta ser más específico con los campos o las fechas.");
      }
    } catch (error) {
      console.error(error);
      alert("❌ Error de comunicación con el asistente inteligente.");
    } finally {
      setCargando(false);
      // 🔥 Activamos el cooldown de 60 segundos pase lo que pase para proteger tu API
      setCooldown(60);
    }
  };

  // 2. Tomar los datos ya impresos en la pantalla y pasarlos limpios para generar el archivo Excel (.xlsx)
  const descargarExcelDesdeVistaPrevia = async () => {
    if (!datosReporte || !datosReporte.filas || datosReporte.filas.length === 0) return;

    setCargandoExcel(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/reportes/ia-descargar-excel/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          titulo: `Reporte Inteligente - ${preguntaIA.slice(0, 40)}`,
          columnas: datosReporte.columnas,
          filas: datosReporte.filas
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Reporte_IA_${new Date().toISOString().slice(0,10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        alert("❌ Ocurrió un error al compilar el archivo Excel en el servidor.");
      }
    } catch (error) {
      console.error(error);
      alert("❌ Error de red al intentar descargar el Excel.");
    } finally {
      setCargandoExcel(false);
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      {/* CUADRO DE TEXTO PARA INSTRUCCIONES DEL ASISTENTE */}
      <div className="bambo-card mb-4" style={{ border: '1px dashed #f7b924' }}>
        <div className="d-flex align-items-center mb-3">
          <div className="bg-gold text-dark p-2 rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
            <i className="bi bi-cpu-fill fs-5"></i>
          </div>
          <div>
            <h5 className="text-gold mb-0">Asistente de Reportes Inteligentes (Gemini AI)</h5>
            <small className="text-secondary">Pídele reportes específicos cruzando datos de Usuarios, Ventas o Pagos de tu base de datos.</small>
          </div>
        </div>

        <form onSubmit={consultarAsistenteIA} className="mt-4">
          <div className="mb-3">
            <textarea 
              className="form-control text-white bg-dark border-secondary"
              rows="3"
              style={{ resize: 'none' }}
              placeholder="Ejemplo: Muéstrame los clientes de marzo que pagaron con yape junto con sus códigos de venta y montos..."
              value={preguntaIA}
              onChange={(e) => setPreguntaIA(e.target.value)}
              disabled={cargando || cargandoExcel || cooldown > 0}
            />
          </div>
          <div className="d-flex justify-content-end align-items-center gap-3">
            {cooldown > 0 && (
              <span className="text-warning small animate__animated animate__pulse animate__infinite">
                <i className="bi bi-hourglass-split me-1"></i> Modo espera activo por saturación...
              </span>
            )}
            
            <button 
              type="submit" 
              className={`btn fw-bold px-4 py-2 ${cooldown > 0 ? 'btn-secondary text-light' : 'bg-gold text-dark'}`} 
              disabled={cargando || cargandoExcel || cooldown > 0}
              style={{ minWidth: '220px' }}
            >
              {cargando ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Gemini analizando...
                </>
              ) : cooldown > 0 ? (
                <>
                  <i className="bi bi-clock-history me-1"></i> Espera {cooldown}s...
                </>
              ) : (
                <>
                  <i className="bi bi-play-fill me-1"></i> Consultar y Mostrar Tabla
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* RENDERIZADO DINÁMICO DE LA VISTA PREVIA */}
      {datosReporte && (
        <div className="bambo-card animate__animated animate__fadeInUp">
          <h5 className="text-white mb-4">
            <i className="bi bi-table text-gold me-2"></i> 
            Vista Previa de los Resultados
          </h5>
          
          <div className="table-responsive">
            <table className="bambo-table">
              <thead>
                <tr>
                  {datosReporte?.columnas?.map((col, index) => (
                    <th key={index}>{col?.toUpperCase()}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {datosReporte?.filas?.map((fila, indexFila) => (
                  <tr key={indexFila}>
                    {fila?.map((celda, indexCelda) => (
                      <td key={indexCelda} className={indexCelda === 0 ? "fw-bold text-gold" : "text-white"}>
                        {celda !== null ? String(celda) : '---'}
                      </td>
                    ))}
                  </tr>
                ))}
                {(!datosReporte?.filas || datosReporte.filas.length === 0) && (
                  <tr>
                    <td colSpan={datosReporte?.columnas?.length || 1} className="text-center text-secondary py-4">
                      No se encontraron registros que coincidan con la descripción dada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* BOTÓN INFERIOR: SE ACTIVA ÚNICAMENTE SI LA TABLA CONTIENE FILAS EN PANTALLA */}
          {datosReporte?.filas?.length > 0 && (
            <div className="d-flex justify-content-center mt-4 pt-3 border-top border-dark">
              <button 
                onClick={descargarExcelDesdeVistaPrevia} 
                className="btn btn-success fw-bold px-5 py-2"
                disabled={cargandoExcel}
                style={{ backgroundColor: '#217346', borderColor: '#1e683f' }}
              >
                {cargandoExcel ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Escribiendo Libro Excel...
                  </>
                ) : (
                  <>
                    <i className="bi bi-file-earmark-excel-fill me-2"></i> Generar y Descargar Excel con estos datos
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- SUB-COMPONENTE: GESTIÓN DE USUARIOS ---
const SeccionUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const cargarUsuarios = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/usuarios/`);
      const data = await response.json();
      const lista = Array.isArray(data) ? data : (data.results || []);
      setUsuarios(lista);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    }
  };

  useEffect(() => { cargarUsuarios(); }, []);

  const cambiarRol = async (id, nuevoRol) => {
    if (!window.confirm(`¿Estás seguro de cambiar el rol a ${nuevoRol}?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/usuarios/${id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ usu_rol: nuevoRol })
      });
      if (response.ok) {
        alert("✅ Rol actualizado correctamente.");
        cargarUsuarios();
      }
    } catch (error) { alert("❌ Error al conectar con el servidor."); }
  };

  const usuariosFiltrados = (usuarios || []).filter(u => {
    const nombres = String(u.usu_nombres || u.Usu_Nombres || '');
    const apellidos = String(u.usu_apellidos || u.Usu_Apellidos || '');
    const email = String(u.usu_email || u.Usu_Email || '');
    const nombreCompleto = `${nombres} ${apellidos}`.toLowerCase();
    return nombreCompleto.includes(busqueda.toLowerCase()) || email.toLowerCase().includes(busqueda.toLowerCase());
  });

  return (
    <div className="bambo-card animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="text-gold mb-0"><i className="bi bi-people me-2"></i>Gestión de Usuarios (Tabla: Usuario)</h5>
        <div className="search-box-table">
          <i className="bi bi-search text-secondary"></i>
          <input type="text" placeholder="Buscar usuario..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
      </div>
      <div className="table-responsive">
        <table className="bambo-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>NOMBRE COMPLETO</th>
              <th>DOCUMENTO</th>
              <th>EMAIL</th>
              <th>ROL ACTUAL</th>
              <th>ACCIONES</th>
            </tr>
          </thead>
          <tbody>
            {usuariosFiltrados?.map((u, index) => {
              const id = u.usu_id ?? u.Usu_ID ?? index;
              return (
                <tr key={id}>
                  <td className="text-secondary">#{id}</td>
                  <td className="fw-bold">{u.usu_nombres || u.Usu_Nombres || '---'} {u.usu_apellidos || u.Usu_Apellidos || ''}</td>
                  <td>
                    <small className="text-secondary">{u.usu_tipodocumento || u.Usu_TipoDocumento || 'DOC'}:</small>{' '}
                    {u.usu_numdocumento || u.Usu_NumDocumento || '---'}
                  </td>
                  <td>{u.usu_email || u.Usu_Email || <span className="text-muted">No registrado</span>}</td>
                  <td>
                    <span className={`status-pill ${String(u.usu_rol || u.Usu_Rol || 'Cliente').toLowerCase()}`}>
                      {u.usu_rol || u.Usu_Rol || 'Cliente'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <button className="btn-action" onClick={() => cambiarRol(id, 'DJ')} title="Asignar DJ"><i className="bi bi-disc"></i></button>
                      <button className="btn-action" onClick={() => cambiarRol(id, 'Asistente')} title="Asignar Asistente"><i className="bi bi-person-badge"></i></button>
                      <button className="btn-action gold" onClick={() => cambiarRol(id, 'Cliente')} title="Resetear a Cliente"><i className="bi bi-arrow-counterclockwise"></i></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {usuariosFiltrados.length === 0 && (
              <tr><td colSpan="6" className="text-center text-secondary py-4">No se encontraron usuarios.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTE: PAGOS ---
const SeccionPagos = () => {
  const [pagos, setPagos] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const cargarPagos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pagos/`);
      if (response.ok) {
        const data = await response.json();
        const listaExtraida = Array.isArray(data) ? data : (data.results || []);
        setPagos(listaExtraida);
      }
    } catch (error) {
      console.error("Error al cargar pagos:", error);
    }
  };

  useEffect(() => { cargarPagos(); }, []);

  const pagosFiltrados = (pagos || []).filter(p => {
    const ref = String(p.pag_referencia || p.Pag_Referencia || '');
    const rawVen = p.ven ?? p.Ven_ID;
    const idVenta = String(rawVen && typeof rawVen === 'object' ? (rawVen.ven_id || rawVen.id) : (rawVen || ''));
    const estado = String(p.pag_estado || p.Pag_Estado || '');
    return ref.toLowerCase().includes(busqueda.toLowerCase()) || 
           idVenta.includes(busqueda) ||
           estado.toLowerCase().includes(busqueda.toLowerCase());
  });

  return (
    <div className="bambo-card animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="text-gold mb-0"><i className="bi bi-credit-card me-2"></i>Historial de Transacciones (Tabla: Pago)</h5>
        <div className="search-box-table">
          <i className="bi bi-search"></i>
          <input 
            type="text" 
            placeholder="Buscar por referencia, venta o estado..." 
            value={busqueda} 
            onChange={(e) => setBusqueda(e.target.value)} 
          />
        </div>
      </div>
      <div className="table-responsive">
        <table className="bambo-table">
          <thead>
            <tr>
              <th>ID PAGO</th>
              <th>COD VENTA</th>
              <th>MONTO</th>
              <th>MÉTODO PAGO (ID/DESC)</th>
              <th>REFERENCIA</th>
              <th>FECHA PAGO</th>
              <th>ESTADO</th>
            </tr>
          </thead>
          <tbody>
            {pagosFiltrados?.map((p, i) => {
              const idPago = p.pag_id ?? p.Pag_ID ?? i;
              const rawVen = p.ven ?? p.Ven_ID;
              const idVenta = rawVen && typeof rawVen === 'object' ? (rawVen.ven_id || rawVen.id) : rawVen;
              
              const rawMetPag = p.metpag ?? p.MetPag_ID;
              let metodo = p.metpag_descripcion || p.MetPag_Descripcion;
              
              if (!metodo && rawMetPag) {
                metodo = typeof rawMetPag === 'object' 
                  ? (rawMetPag.metpag_descripcion || rawMetPag.MetPag_Descripcion) 
                  : `Método ID: ${rawMetPag}`;
              }
              if (!metodo) metodo = 'No especificado';

              const fechaRaw = p.pag_fecha ?? p.Pag_Fecha;
              const fecha = fechaRaw ? new Date(fechaRaw).toLocaleDateString('es-PE') : '---';
              const estado = p.pag_estado || p.Pag_Estado || 'Registrado';

              return (
                <tr key={idPago}>
                  <td className="fw-bold text-gold">#{idPago}</td>
                  <td>{idVenta ? `Venta #${idVenta}` : 'Gral'}</td>
                  <td className="fw-bold">S/ {parseFloat(p.pag_monto || p.Pag_Monto || 0).toFixed(2)}</td>
                  <td>
                    <span className={`badge-metodo ${String(metodo).toLowerCase().replace(/\s+/g, '')}`}>
                      {metodo}
                    </span>
                  </td>
                  <td className="text-secondary"><small>{p.pag_referencia || p.Pag_Referencia || 'Sin Referencia'}</small></td>
                  <td>{fecha}</td>
                  <td><span className={`status-pill ${String(estado).toLowerCase()}`}>{estado}</span></td>
                </tr>
              );
            })}
            {pagosFiltrados.length === 0 && (
              <tr><td colSpan="7" className="text-center text-secondary py-4">No se encontraron registros en la tabla Pago.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTE: SERVICIOS ---
const SeccionServicios = () => {
  const [ventas, setVentas] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const cargarVentas = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ventas/`);
      if (response.ok) {
        const data = await response.json();
        const listaExtraida = Array.isArray(data) ? data : (data.results || []);
        setVentas(listaExtraida);
      }
    } catch (error) {
      console.error("Error al cargar ventas:", error);
    }
  };

  useEffect(() => { cargarVentas(); }, []);

  const ventasFiltradas = (ventas || []).filter(v => {
    const estado = String(v.ven_estado || v.Ven_Estado || '');
    const idVenta = String(v.ven_id || v.Ven_ID || '');
    return estado.toLowerCase().includes(busqueda.toLowerCase()) || idVenta.includes(busqueda);
  });

  return (
    <div className="bambo-card animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="text-gold mb-0"><i className="bi bi-box-seam me-2"></i>Servicios Contratados (Tabla: Venta)</h5>
        <div className="search-box-table">
          <i className="bi bi-search"></i>
          <input 
            type="text" 
            placeholder="Buscar por código o estado..." 
            value={busqueda} 
            onChange={(e) => setBusqueda(e.target.value)} 
          />
        </div>
      </div>
      <div className="table-responsive">
        <table className="bambo-table">
          <thead>
            <tr>
              <th>COD VENTA</th>
              <th>CLIENTE (ID)</th>
              <th>DJ (ID)</th>
              <th>PAQUETE / RECURSO</th>
              <th>DURACIÓN</th>
              <th>FECHA EVENTO</th>
              <th>MONTO TOTAL</th>
              <th>ESTADO</th>
            </tr>
          </thead>
          <tbody>
            {ventasFiltradas?.map((v, i) => {
              const idVenta = v.ven_id ?? v.Ven_ID ?? i;
              const rawCli = v.cli ?? v.Cli_ID;
              const idCliente = rawCli && typeof rawCli === 'object' ? (rawCli.cli_id || rawCli.id) : rawCli;
              
              const rawDj = v.dj ?? v.DJ_ID;
              const idDj = rawDj && typeof rawDj === 'object' ? (rawDj.dj_id || rawDj.id) : rawDj;
              
              const rawPaq = v.paq ?? v.Paq_ID;
              let infoPaquete = v.paq_nombre || v.Paq_Nombre;
              if (!infoPaquete && rawPaq) {
                infoPaquete = typeof rawPaq === 'object' 
                  ? (rawPaq.paq_nombre || rawPaq.Paq_Nombre) 
                  : `Paquete ID: #${rawPaq}`;
              }
              if (!infoPaquete) infoPaquete = 'Por definir';

              const fechaRaw = v.ven_fechaevento ?? v.Ven_FechaEvento;
              const fecha = fechaRaw ? new Date(fechaRaw).toLocaleDateString('es-PE') : '---';
              const estado = v.ven_estado || v.Ven_Estado || 'Vendido';

              return (
                <tr key={idVenta}>
                  <td className="fw-bold">#{String(idVenta).padStart(4, '0')}</td>
                  <td>{idCliente ? `ID: ${idCliente}` : '---'}</td>
                  <td>{idDj ? `ID: ${idDj}` : '---'}</td>
                  <td className="text-gold fw-bold">{infoPaquete}</td>
                  <td>{v.ven_duracionhoras || v.Ven_DuracionHoras || 0} horas</td>
                  <td>{fecha}</td>
                  <td className="fw-bold text-white">S/ {parseFloat(v.ven_montototal || v.Ven_MontoTotal || 0).toFixed(2)}</td>
                  <td><span className={`status-pill ${String(estado).toLowerCase()}`}>{estado}</span></td>
                </tr>
              );
            })}
            {ventasFiltradas.length === 0 && (
              <tr><td colSpan="8" className="text-center text-secondary py-4">No se encontraron registros en la tabla Venta.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- SUB-COMPONENTE: EQUIPO DJ ---
const SeccionDJ = () => {
  const listaDJ = [
    { cargo: 'DJ Principal', nombre: 'Cotera Ponce Deyvid', pago: 'S/ 550', estado: 'disponible', ubicacion: 'Huancayo' },
    { cargo: 'DJ Residente', nombre: 'García Torres Luis', pago: 'S/ 650', estado: 'disponible', ubicacion: 'Huancayo' },
  ];
  return (
    <div className="bambo-card animate__animated animate__fadeIn">
      <h5 className="mb-4 text-gold"><i className="bi bi-disc me-2"></i>Personal DJ Activo</h5>
      <div className="table-responsive">
        <table className="bambo-table">
          <thead><tr><th>CARGO</th><th>NOMBRE</th><th>PAGO BASE</th><th>ESTADO</th><th>UBICACIÓN</th></tr></thead>
          <tbody>
            {listaDJ?.map((dj, i) => (
              <tr key={i}><td>{dj.cargo}</td><td className="fw-bold">{dj.nombre}</td><td className="text-gold">{dj.pago}</td><td><span className="status-pill disponible">{dj.estado}</span></td><td>{dj.ubicacion}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- COMPONENTE PRINCIPAL DASHBOARD ---
const DashboardAdmin = () => {
  const [pestana, setPestana] = useState('inicio');
  const [userName, setUserName] = useState('');
  const [totalRecaudado, setTotalRecaudado] = useState(0);
  const [eventosPendientes, setEventosPendientes] = useState(0);
  
  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem('role');
    const user = localStorage.getItem('username');
    if (role !== 'admin') { 
      navigate('/login', { replace: true }); 
    } else { 
      setUserName(user || 'Admin'); 
    }

    const cargarMetricasHome = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pagos/`);
        if (response.ok) {
          const data = await response.json();
          const listaPagos = Array.isArray(data) ? data : (data.results || []);
          
          const suma = listaPagos.reduce((acc, curr) => acc + parseFloat(curr.pag_monto || curr.Pag_Monto || 0), 0);
          setTotalRecaudado(suma);

          const pendientes = listaPagos.filter(p => String(p.pag_estado || p.Pag_Estado || '').toLowerCase() === 'pendiente').length;
          setEventosPendientes(pendientes);
        }
      } catch (error) {
        console.error("Error cargando métricas iniciales:", error);
      }
    };

    cargarMetricasHome();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login', { replace: true });
  };

  const dataVentas = [
    { day: 'Lun', valor: 400 }, { day: 'Mar', valor: 300 },
    { day: 'Mie', valor: 600 }, { day: 'Jue', valor: 800 }, 
    { day: 'Vie', valor: 500 }, { day: 'Sab', valor: 900 }, { day: 'Dom', valor: 700 }
  ];

  return (
    <div className="bambo-app-container">
      <aside className="bambo-sidebar">
        <div className="sidebar-header">
          <img src="/Logo-BamboAct.png" alt="Logo" style={{ height: '50px' }} />
        </div>
        <nav className="sidebar-nav">
          <button className={`nav-item ${pestana === 'inicio' ? 'active' : ''}`} onClick={() => setPestana('inicio')}><i className="bi bi-speedometer2"></i> Dashboard</button>
          <button className={`nav-item ${pestana === 'usuarios' ? 'active' : ''}`} onClick={() => setPestana('usuarios')}><i className="bi bi-people"></i> Usuarios</button>
          <button className={`nav-item ${pestana === 'servicios' ? 'active' : ''}`} onClick={() => setPestana('servicios')}><i className="bi bi-box-seam"></i> Servicios</button>
          <button className={`nav-item ${pestana === 'pagos' ? 'active' : ''}`} onClick={() => setPestana('pagos')}><i className="bi bi-credit-card"></i> Pagos</button>
          <button className={`nav-item ${pestana === 'dj' ? 'active' : ''}`} onClick={() => setPestana('dj')}><i className="bi bi-disc"></i> Equipo DJ</button>
          <button className={`nav-item ${pestana === 'reportes_ia' ? 'active' : ''}`} onClick={() => setPestana('reportes_ia')} style={{ borderLeft: '3px solid #f7b924' }}><i className="bi bi-cpu-fill text-gold"></i> Reportes IA</button>
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleLogout} className="btn-logout"><i className="bi bi-box-arrow-left"></i> Cerrar Sesión</button>
        </div>
      </aside>

      <main className="bambo-main-content">
        <header className="top-header">
          <div className="header-search">
            <i className="bi bi-search text-secondary"></i>
            <input type="text" placeholder="Buscar en el sistema..." />
          </div>
          <div className="user-profile">
            <div className="user-info text-end">
              <span className="user-name">{userName}</span>
              <span className="user-role text-gold">Super Admin</span>
            </div>
            <div className="avatar">{userName.charAt(0).toUpperCase()}</div>
          </div>
        </header>

        <section className="content-body">
          {pestana === 'inicio' && (
            <div className="animate__animated animate__fadeIn">
              <div className="metrics-row mb-4">
                <div className="stat-card">
                  <span className="stat-label">Recaudación de Pagos</span>
                  <h2 className="stat-value">S/ {totalRecaudado.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</h2>
                  <span className="stat-change positive">+15% este período</span>
                </div>
                <div className="stat-card">
                  <span className="stat-label">Alertas Pendientes</span>
                  <h2 className="stat-value">{eventosPendientes}</h2>
                  <span className="stat-change text-secondary">Por revisar</span>
                </div>
              </div>
              <div className="row g-4">
                <div className="col-lg-8">
                  <div className="bambo-card h-100">
                    <h5 className="mb-4"><i className="bi bi-graph-up me-2 text-gold"></i>Flujo de Ventas Semanal</h5>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={dataVentas}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#333" />
                        <XAxis dataKey="day" stroke="#888" axisLine={false} tick={{fontSize: 12}} />
                        <YAxis stroke="#888" axisLine={false} tick={{fontSize: 12}} />
                        <Tooltip contentStyle={{backgroundColor: '#111', border: '1px solid #333', color: '#fff'}} />
                        <Line type="monotone" dataKey="valor" stroke="#f7b924" strokeWidth={4} dot={{r:6, fill:'#f7b924'}} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="bambo-card h-100 text-center">
                    <h5 className="mb-4">Estado General</h5>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={[{v: 85}, {v: 15}]} dataKey="v" innerRadius={60} outerRadius={80} paddingAngle={10}>
                          <Cell fill="#f7b924" /><Cell fill="#222" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="d-flex justify-content-around mt-3">
                        <small><i className="bi bi-circle-fill text-gold me-1"></i> Operativo</small>
                        <small><i className="bi bi-circle-fill text-secondary me-1"></i> En Espera</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {pestana === 'usuarios' && <SeccionUsuarios />}
          {pestana === 'servicios' && <SeccionServicios />}
          {pestana === 'pagos' && <SeccionPagos />}
          {pestana === 'dj' && <SeccionDJ />}
          {pestana === 'reportes_ia' && <SeccionReportesIA />}
        </section>
      </main>
    </div>
  );
};

export default DashboardAdmin;